import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { UserExperienceService } from 'src/app/api/services/user-experience.service';
import { AddExperienceComponent } from './components/add-experience/add-experience.component';
import { UpdateExperinceComponent } from './components/update-experience/update-experience.component';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { TokenStorageService } from 'src/app/shared/service/token-storage.service';
import { MESSAGES } from 'src/app/core/constant/messages';
import { ToastService } from 'src/app/shared/service/toast.service';
import {
  heroBriefcase,
  heroBuildingOffice,
  heroCalendarDays,
  heroPencilSquare,
  heroPhone,
  heroPlus,
  heroTrash,
} from '@ng-icons/heroicons/outline';
import { finalize } from 'rxjs';
import { LoadingComponent } from 'src/app/shared/component/loading/loading.component';
import { handleApiError } from 'src/app/shared/util/handle-api-error';

@Component({
  selector: 'app-profile-experince',
  imports: [
    CommonModule,
    NgIcon,
    AddExperienceComponent,
    UpdateExperinceComponent,
    LoadingButtonComponent,
    LoadingComponent,
  ],
  templateUrl: './profile-experince.component.html',
  viewProviders: [
    provideIcons({
      heroBriefcase,
      heroPlus,
      heroBuildingOffice,
      heroCalendarDays,
      heroPhone,
      heroPencilSquare,
      heroTrash,
    }),
  ],
})
export class ProfileExperinceComponent implements OnInit {
  experiences: UserExperienceResponse[] = [];

  showAddModal: boolean = false;
  showUpdatingModal: boolean = false;
  showDeleteModal: boolean = false;
  isDeleting: boolean = false;
  isLoading: boolean = false;

  selectedExperience: UserExperienceResponse | null = null;
  selectedDeleteExperience: UserExperienceResponse | null = null;

  constructor(
    private userExpService: UserExperienceService,
    private tokenStorage: TokenStorageService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData() {
    const authData = this.tokenStorage.getAuth();
    if (!authData?.user?.idUser) return;

    this.isLoading = true;
    this.userExpService
      .getExperiencesByUser(authData.user.idUser)
      .pipe(
        handleApiError(this.toastService, MESSAGES.USER.EXPERIENCE_LOAD_ERROR),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (res: any) => {
          this.experiences = res.dto.experiences || [];
        },
      });
  }

  openAddModal() {
    this.showAddModal = true;
  }

  closeAddModal() {
    this.showAddModal = false;
  }

  openUpdateModal(exp: UserExperienceResponse) {
    this.selectedExperience = exp;
    this.showUpdatingModal = true;
  }

  closeUpdateModal() {
    this.selectedExperience = null;
    this.showUpdatingModal = false;
  }

  openDeleteModal(exp: UserExperienceResponse) {
    this.selectedDeleteExperience = exp;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.selectedDeleteExperience = null;
    this.showDeleteModal = false;
  }

  onSave() {
    const authData = this.tokenStorage.getAuth();
    if (authData && authData.user?.idUser) {
      this.userExpService
        .getExperiencesByUser(authData.user?.idUser)
        .subscribe({
          next: (res) => {
            this.experiences = res.dto.experiences || [];
            this.closeAddModal();
          },
          error: () => {
            this.experiences = [];
            this.closeAddModal();
          },
        });
    }
  }

  onUpdate(updated: UserExperienceResponse) {
    this.experiences = this.experiences.map((exp) =>
      exp.idUserExperience === updated.idUserExperience ? updated : exp
    );
    this.closeUpdateModal();
  }

  confirmDelete() {
    if (!this.selectedDeleteExperience?.idUserExperience) return;
    this.isDeleting = true;

    this.userExpService
      .deleteExperience(this.selectedDeleteExperience.idUserExperience)
      .pipe(
        handleApiError(
          this.toastService,
          MESSAGES.USER.EXPERIENCE_DELETE_ERROR
        ),
        finalize(() => {
          this.isDeleting = false;
          this.closeDeleteModal();
        })
      )
      .subscribe({
        next: () => {
          this.toastService.showMessages('success', [
            MESSAGES.USER.EXPERIENCE_DELETE_SUCCESS,
          ]);
          this.loadData();
        },
      });
  }
}
